import CardWithBackgroundImage from 'components/CardWithBackgroundImage';
import TokenRedemptionCostPill from 'components/token-town/TokenRedemptionCostPill';
import { ReactNode } from 'react';
import { extendClassName } from 'utils/common';
import { ASSET_PREFIX, colors } from 'utils/constants';
import DrawCountdownString from './draws/draw-countdown/DrawCountdown';

interface IImageOverlayProps {
  unlocked?: boolean;
  redemptionCost: number;
  hidePadlockIcon?: boolean;
  showGuaranteedWinnerBanner?: boolean;
  nextDrawDate?: Date | null;
}

const ImageOverlay = ({
  unlocked,
  redemptionCost,
  hidePadlockIcon,
  showGuaranteedWinnerBanner,
  nextDrawDate,
}: IImageOverlayProps) => (
  <div className="imageOverlay flexCentered flexSpaceBetween">
    <div className="topIcons flexRow flexSpaceBetween">
      <TokenRedemptionCostPill redemptionCost={redemptionCost} />
      {!hidePadlockIcon &&
        (unlocked ? (
          <img
            className="padlockImg"
            src={`${ASSET_PREFIX}/images/token-town/arcade-padlock-unlocked.svg`}
          />
        ) : (
          <img
            className="padlockImg"
            src={`${ASSET_PREFIX}/images/token-town/arcade-padlock-locked.svg`}
          />
        ))}
    </div>

    {nextDrawDate ? (
      <div className="banner flexCentered flexRow">
        <img src={`${ASSET_PREFIX}/images/token-town/clock-icon.svg`} />
        <h5 className="drawTimerText">
          NEXT DRAW IN <br />
          <DrawCountdownString date={nextDrawDate} />
        </h5>
        <img src={`${ASSET_PREFIX}/images/token-town/clock-icon.svg`} />
      </div>
    ) : (
      showGuaranteedWinnerBanner && (
        <div className="banner flexCentered flexRow">
          <img src={`${ASSET_PREFIX}/images/token-town/trophy-icon.svg`} />
          <h5>GUARANTEED WINNER</h5>
          <img src={`${ASSET_PREFIX}/images/token-town/trophy-icon.svg`} />
        </div>
      )
    )}

    <style jsx>
      {`
        .imageOverlay {
          width: 100%;
          height: 100%;
        }

        .padlockImg {
          height: 20px;
          margin-left: 5%;
        }

        .topIcons {
          padding: 6%;
          width: 100%;
        }

        .banner {
          background-color: #ffffffbb;
          width: 100%;
          padding: 0.75em 0.5em;
          min-height: 35px;
        }

        .banner img {
          width: 6%;
          min-width: 10px;
          max-width: 20px;
        }

        .banner h5 {
          font-size: 0.7em;
          font-weight: bold;
          margin: 0 4%;
          text-align: center;
          line-height: 1.2em;
          text-transform: uppercase;
        }

        .drawTimerText {
          min-width: 115px;
        }

        @media (min-width: 500px) {
          .padlockImg {
            height: 32px;
          }

          .banner {
            min-height: 50px;
          }
          
          .banner h5 {
            font-size: 1.1em;
            font-weight: 800;
          }

          .drawTimerText {
            min-width: 150px;
          }
        }
      `}
    </style>
  </div>
);

interface IGenericTokenRedemptionCardProps {
  imagePath?: string;
  imageFname?: string;
  tokenBalance: number;
  noBorder?: boolean;
  fallbackImagePath?: string;
  imageOpacity?: number;
  unlocked?: boolean;
  redemptionCost: number;
  hidePadlockIcon?: boolean;
  showGuaranteedWinnerBanner?: boolean;
  nextDrawDate?: Date | null;
  redemptionOptionLocked?: boolean;
  onClick?: () => void;
  className?: string;
  children?: ReactNode;
}

const GenericTokenRedemptionCard = ({
  redemptionCost,
  imagePath,
  imageFname,
  tokenBalance,
  noBorder,
  hidePadlockIcon,
  showGuaranteedWinnerBanner,
  nextDrawDate,
  redemptionOptionLocked,
  onClick,
  className,
  children,
}: IGenericTokenRedemptionCardProps) => {
  const unlocked = !redemptionOptionLocked && tokenBalance >= redemptionCost;
  return (
    <div
      className={extendClassName(
        'genericTokenRedemptionCard flexCentered',
        className
      )}
      onClick={onClick}
    >
      <div className="topSection">
        {imagePath ? (
          <CardWithBackgroundImage
            src={imagePath}
            fallbackImagePath={imagePath}
          >
            <ImageOverlay
              unlocked={unlocked}
              redemptionCost={redemptionCost}
              hidePadlockIcon={hidePadlockIcon}
              showGuaranteedWinnerBanner={showGuaranteedWinnerBanner}
              nextDrawDate={nextDrawDate}
            />
          </CardWithBackgroundImage>
        ) : (
          <CardWithBackgroundImage
            imageDirectory="token-town"
            imageFname={imageFname}
          >
            <ImageOverlay
              unlocked={unlocked}
              redemptionCost={redemptionCost}
              hidePadlockIcon={hidePadlockIcon}
              showGuaranteedWinnerBanner={showGuaranteedWinnerBanner}
              nextDrawDate={nextDrawDate}
            />
          </CardWithBackgroundImage>
        )}
      </div>

      <div className="bottomSection">{children}</div>

      <style jsx>
        {`
          .genericTokenRedemptionCard {
            width: 100%;
            box-shadow: 1px 1px 4px ${colors.primaryBrandGray};
            background: #ffffff;
            border-radius: 5px;
            border: 3px solid ${unlocked ? '#50E3C2' : '#FB55B0'};
            border-width: 3px;
            cursor: ${onClick ? 'pointer' : 'default'};
            justify-content: flex-start;
            ${noBorder ? 'border: none;' : ''}
            ${noBorder ? 'box-shadow: none;' : ''}
          }

          :global(
              .genericTokenRedemptionCard .cardWithBackgroundImage .children
            ) {
            justify-content: flex-start;
          }

          .topSection {
            width: 100%;
            padding-top: 85%;
            position: relative;
          }

          :global(.topSection .cardWithBackgroundImage) {
            z-index: 1;
            position: absolute;
            top: 0;
            left: 0;
            bottom: 0;
            right: 0;
          }

          .bottomSection {
            padding: 7% 5% 10%;
            width: 100%;
            display: flex;
            flex-direction: column;
            flex-grow: 1;
            flex-shrink: 0;
          }

          :global(.genericTokenRedemptionCard h4),
          :global(.genericTokenRedemptionCard p) {
            text-align: left;
          }

          @media (max-width: 800px) {
            .bottomSection {
              font-size: 0.7rem;
            }
          }

          @media (min-width: 700px) {
            .genericTokenRedemptionCard {
              border-width: 6px;
            }

            .topSection {
              padding-top: 80%;
            }
          }
        `}
      </style>
    </div>
  );
};

export default GenericTokenRedemptionCard;
